import React, { useState } from 'react';
import { Container, Card, Button, Table, Alert } from 'react-bootstrap';

const FieldNameTest = () => {
  const [testResults, setTestResults] = useState(null);

  // Test different variations of the Country of Origin field name
  const testFieldNames = [
    "Country of Origin",
    "Country of Origin ", // with trailing space
    " Country of Origin", // with leading space
    "Country of Origin\u00A0", // with non-breaking space
    "Country\u00A0of\u00A0Origin", // with non-breaking spaces
    "Country of Origin\t", // with tab
    "Country of Origin\n", // with newline
    "Country of Origin\r", // with carriage return
  ];

  const runFieldNameTest = () => {
    const results = testFieldNames.map(fieldName => {
      const charCodes = fieldName.split('').map(char => char.charCodeAt(0));
      const trimmed = fieldName.trim();
      const hasWhitespace = fieldName !== trimmed;
      const hasSpecialChars = charCodes.some(code => code > 127 || (code < 32 && code !== 9 && code !== 10 && code !== 13));
      
      return {
        original: fieldName,
        length: fieldName.length,
        trimmed: trimmed,
        trimmedLength: trimmed.length,
        hasWhitespace: hasWhitespace,
        hasSpecialChars: hasSpecialChars,
        charCodes: charCodes,
        exactMatch: fieldName === "Country of Origin",
        trimmedMatch: trimmed === "Country of Origin"
      };
    });

    setTestResults(results);
    
    // Log detailed analysis
    console.log('=== FIELD NAME ANALYSIS ===');
    results.forEach((result, index) => {
      console.log(`Test ${index + 1}:`);
      console.log(`  Original: "${result.original}"`);
      console.log(`  Length: ${result.length}`);
      console.log(`  Char codes: [${result.charCodes.join(', ')}]`);
      console.log(`  Has whitespace: ${result.hasWhitespace}`);
      console.log(`  Has special chars: ${result.hasSpecialChars}`);
      console.log(`  Exact match: ${result.exactMatch}`);
      console.log(`  Trimmed match: ${result.trimmedMatch}`);
      console.log('---');
    });
  };

  // Test the actual field from the extracted JSON
  const testActualField = () => {
    // This simulates the actual field name from the extracted JSON
    const actualFieldName = "Country of Origin";
    
    console.log('=== ACTUAL FIELD TEST ===');
    console.log(`Field name: "${actualFieldName}"`);
    console.log(`Length: ${actualFieldName.length}`);
    console.log(`Char codes: [${actualFieldName.split('').map(c => c.charCodeAt(0)).join(', ')}]`);
    
    // Test exact matching
    const testStrings = [
      "Country of Origin",
      "country of origin",
      "COUNTRY OF ORIGIN"
    ];
    
    testStrings.forEach(testString => {
      console.log(`"${actualFieldName}" === "${testString}": ${actualFieldName === testString}`);
      console.log(`"${actualFieldName}".toLowerCase() === "${testString}".toLowerCase(): ${actualFieldName.toLowerCase() === testString.toLowerCase()}`);
    });
  };

  return (
    <Container className="mt-4">
      <Card>
        <Card.Header>
          <h4>Field Name Test - Character Analysis</h4>
        </Card.Header>
        <Card.Body>
          <p>This test analyzes different variations of the "Country of Origin" field name to check for character encoding issues.</p>
          
          <div className="mb-3">
            <Button onClick={runFieldNameTest} className="me-2">
              Test Field Name Variations
            </Button>
            <Button onClick={testActualField} variant="secondary">
              Test Actual Field (Check Console)
            </Button>
          </div>

          {testResults && (
            <div>
              <Alert variant="info">
                <h6>Field Name Analysis Results</h6>
                <p>Check the table below and browser console for detailed analysis.</p>
              </Alert>

              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Test #</th>
                    <th>Field Name</th>
                    <th>Length</th>
                    <th>Has Whitespace?</th>
                    <th>Has Special Chars?</th>
                    <th>Exact Match?</th>
                    <th>Trimmed Match?</th>
                  </tr>
                </thead>
                <tbody>
                  {testResults.map((result, index) => (
                    <tr key={index} style={{
                      backgroundColor: result.exactMatch ? '#d4edda' : 
                                     result.trimmedMatch ? '#fff3cd' : 'transparent'
                    }}>
                      <td>{index + 1}</td>
                      <td>
                        <code>"{result.original}"</code>
                        <div className="small text-muted">
                          Char codes: [{result.charCodes.join(', ')}]
                        </div>
                      </td>
                      <td>{result.length}</td>
                      <td>{result.hasWhitespace ? '⚠️ YES' : '✅ No'}</td>
                      <td>{result.hasSpecialChars ? '⚠️ YES' : '✅ No'}</td>
                      <td>{result.exactMatch ? '✅ YES' : '❌ No'}</td>
                      <td>{result.trimmedMatch ? '✅ YES' : '❌ No'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="mt-3">
                <h6>Legend:</h6>
                <ul>
                  <li><span style={{backgroundColor: '#d4edda', padding: '2px 4px'}}>Green</span> = Exact match with "Country of Origin"</li>
                  <li><span style={{backgroundColor: '#fff3cd', padding: '2px 4px'}}>Yellow</span> = Matches after trimming whitespace</li>
                  <li>White = No match</li>
                </ul>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default FieldNameTest;
